import React, { useState } from "react";
import { Link } from "react-router-dom";
import "./Aivionsales.css";
import { navigateToHomeSection } from '../utils/navigation';

const capabilities = [
  {
    id: "01",
    title: "Lead & Opportunity Scoring",
    short: "Propensity models that rank leads and deals by likelihood to close.",
    detail: "Propensity models that rank leads and deals by likelihood to close.",
  },
  {
    id: "02",
    title: "Next-Best-Action",
    short: "Recommendations for follow-up timing, channel and content per account.",
    detail: "Recommendations for follow-up timing, channel and content per account.",
  },
  {
    id: "03",
    title: "Forecast Intelligence",
    short: "Probabilistic pipeline forecasts with scenario analysis for leadership.",
    detail: "Probabilistic pipeline forecasts with scenario analysis for leadership.",
  },
  {
    id: "04",
    title: "CRM Automation",
    short: "Auto-log activities, update fields and draft follow-ups from calls and emails.",
    detail: "Auto-log activities, update fields and draft follow-ups from calls and emails.",
  },
  {
    id: "05",
    title: "Churn & Expansion Signals",
    short: "Early warnings for renewal risk and whitespace opportunities in existing accounts.",
    detail: "Early warnings for renewal risk and whitespace opportunities in existing accounts.",
  },
  {
    id: "06",
    title: "Salesforce & HubSpot Native",
    short: "Deep integration with leading CRM platforms — no rip-and-replace required.",
    detail: "Deep integration with leading CRM platforms — no rip-and-replace required.",
  },
];

const deploySteps = [
  { id: "01", title: "Connect", desc: "Ingest CRM, marketing and product usage data into a unified revenue model." },
  { id: "02", title: "Model", desc: "Build and validate scoring and forecasting models against historical outcomes." },
  { id: "03", title: "Activate", desc: "Embed scores and recommendations into daily rep workflows and leadership dashboards." },
  { id: "04", title: "Improve", desc: "Continuously refine models as win/loss data and market conditions evolve." },
];

const outcomes = [
  "Higher win rates through better prioritisation and timing",
  "More accurate forecasts that leadership can trust",
  "20–30% more selling time by automating CRM admin",
  "Earlier churn detection and expansion opportunities surfaced",
  "Faster ramp for new reps with AI-guided playbooks",
];

const products = [
  { name: "aivionGrade", tag: "AI-Powered Exam Assessment", link: "https://aiviongrade.aivion.ai/" },
  { name: "aivionScore", tag: "AI-Based Credit Review", link: "/aivionscore" },
  { name: "DigitalDeputee", tag: "Digital Employees Platform", link: "/digitaldeputee" },
];

const navLinks = [
  { label: "Services", href: "/#services" },
  { label: "Products", href: "/#products" },
  { label: "Industries", href: "/#industries" },
  { label: "Partners", href: "/#clients" },
  { label: "About", href: "/#about" },
  { label: "How We Work", href: "/#how-we-work" },
  { label: "Case Studies", href: "/#case-studies" },
  { label: "Contact Us", href: "/#contact" },
];
const footerLinks = [
  { label: "Services", href: "/#services" },
  { label: "Products", href: "/#products" },
  { label: "Industries", href: "/#industries" },
  { label: "Partners", href: "/#clients" },
  { label: "About", href: "/#about" },
  { label: "How We Work", href: "/#how-we-work" },
  { label: "Case Studies", href: "/#case-studies" },
  { label: "Contact Us", href: "/#contact" },
];

export default function AivionSales() {
  const [active, setActive] = useState(0);

  return (
    <div className="av-root">

      {/* ── NAV ── */}
      <nav className="av-nav">
        <div className="av-nav-inner">
          <div className="av-logo-box">
            <span className="av-logo-aiv">AIV</span><span className="av-logo-ion">ION</span>
          </div>
          <ul className="av-nav-links">
            {navLinks.map((l) => <li key={l.label}><a href={l.href} onClick={(e)=>{e.preventDefault(); navigateToHomeSection(l.href);}}>{l.label}</a></li>)}
          </ul>
          <a href="/#contact" className="av-contact-btn" onClick={(e)=>{e.preventDefault(); navigateToHomeSection('/#contact');}}>CONTACT US</a>
        </div>
      </nav>

      {/* ── HERO ── */}
      <section className="av-hero-wrap">
        <div className="av-hero">
          <div className="av-hero-left">
            <div className="av-breadcrumb">
              <span className="av-bc-arrow">&#8592;</span>
              <span className="av-bc-parent">AIVION PRODUCTS</span>
              <span className="av-bc-badge">&#9679; PRODUCT · LIVE</span>
            </div>
            <h1 className="av-hero-h1">aivionSales</h1>
            <p className="av-hero-sub">AI-Based Sales & CRM</p>
            <div className="av-hero-ctas">
              <a href="/#contact" className="av-btn-solid-outline" onClick={(e)=>{e.preventDefault(); navigateToHomeSection('/#contact');}}>REQUEST A DEMO &#8594;</a>
              <button className="av-btn-ghost" onClick={(e)=>{e.preventDefault(); navigateToHomeSection('/#case-studies');}}>TAKE THE TOUR</button>
            </div>
            <div className="av-hero-tags">
              <span>&#10011; ENTERPRISE-GRADE</span>
              <span>&#9711; WORKFLOW-NATIVE</span>
              <span>&#10022; DEPLOYS IN WEEKS</span>
            </div>
          </div>
          <div className="av-hero-right">
            <div className="av-browser">
              <div className="av-browser-topbar">
                <span className="av-dot" /><span className="av-dot" /><span className="av-dot" />
                <div className="av-urlbar">app.aivion.ai/aivion-score</div>
              </div>
              <div className="av-browser-screen">
                <div className="av-browser-inner-rect" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── BUILT END TO END ── */}
      <section className="av-built">
        <div className="av-built-inner">
          <div className="av-built-left">
            <p className="av-eyebrow">THE PRODUCT</p>
            <h2 className="av-built-title">Built to do the job,<br />end-to-end.</h2>
          </div>
          <div className="av-built-right">
            <p>
              aivionSales gives revenue teams the intelligence to prioritise the right opportunities, forecast with confidence and automate low-value CRM work. Built for B2B sales organisations with complex pipelines, it combines propensity scoring, next-best-action recommendations and GenAI-assisted outreach — all grounded in your CRM data and governed under your sales methodology. Leaders get accurate forecasts; reps get more selling time.
            </p>
          </div>
        </div>
      </section>

      {/* ── GUIDED TOUR ── */}
      <section id="product-tour" className="av-tour">
        <div className="av-tour-top">
          <div className="av-tour-top-left">
            <p className="av-eyebrow-sm">INSIDE THE PLATFORM</p>
            <h2 className="av-tour-title">A guided tour</h2>
          </div>
          <p className="av-tour-hint">
            Click any capability to see what aivionSales does — and how it shows up in your day-to-day.
          </p>
        </div>

        <div className="av-tour-body">
          {/* left list */}
          <ul className="av-cap-list">
            {capabilities.map((cap, i) => (
              <li
                key={cap.id}
                className={`av-cap-item${active === i ? " av-cap-active" : ""}`}
                onClick={() => setActive(i)}
              >
                <span className={`av-cap-num${active === i ? " av-cap-num-active" : ""}`}>{cap.id}</span>
                <div className="av-cap-body">
                  <strong>{cap.title}</strong>
                  <span>{cap.short}</span>
                </div>
                {active === i && <span className="av-cap-arr">&#8594;</span>}
              </li>
            ))}
          </ul>

          {/* right detail — two cards stacked */}
          <div className="av-detail-wrap">
            <div className="av-detail-top-card">
              <p className="av-detail-label">CAPABILITY {capabilities[active].id}</p>
              <h3 className="av-detail-title">{capabilities[active].title}</h3>
            </div>
            <div className="av-detail-bottom-card">
              <p>{capabilities[active].detail}</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── DEPLOY ── */}
      <section className="av-deploy">
        <div className="av-deploy-inner">
          <p className="av-eyebrow av-center">FROM SIGN-OFF TO LIVE</p>
          <h2 className="av-deploy-title">How aivionSales gets deployed</h2>
          <div className="av-deploy-grid">
            {deploySteps.map((s) => (
              <div key={s.id} className="av-deploy-col">
                <div className="av-deploy-circle">{s.id}</div>
                <div className="av-deploy-card">
                  <h4>{s.title}</h4>
                  <p>{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── OUTCOMES ── */}
      <section className="av-outcomes">
        <div className="av-outcomes-inner">
          <div className="av-outcomes-left">
            <p className="av-eyebrow-sm">WHY TEAMS CHOOSE IT</p>
            <h2 className="av-outcomes-title">Outcomes, not<br />output.</h2>
            <p className="av-outcomes-desc">
              aivionSales is purpose-built to move the metrics your team is measured on — not just to add another dashboard.
            </p>
          </div>
          <div className="av-outcomes-grid">
            {outcomes.map((o, i) => (
              <div key={i} className="av-outcome-card">
                <span className="av-outcome-check">&#10003;</span>
                <p>{o}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── MORE PRODUCTS ── */}
      <section className="av-more">
        <div className="av-more-inner">
          <div className="av-more-top">
            <div>
              <p className="av-eyebrow-sm">THE PRODUCT FAMILY</p>
              <h2 className="av-more-title">More from AIVION</h2>
            </div>
            <a className="av-more-all" href="/#products">All products &#8594;</a>
          </div>
          <div className="av-more-grid">
            {products.map((p) => (
              p.link.startsWith("http") ? (
                <a key={p.name} href={p.link} target="_blank" rel="noreferrer" className="av-prod-card">
                  <div className="av-prod-top">
                    <span className="av-prod-tag">PRODUCT</span>
                    <span className="av-prod-arrow">↗</span>
                  </div>
                  <h4>{p.name}</h4>
                  <p>{p.tag}</p>
                </a>
              ) : (
                <Link key={p.name} to={p.link} className="av-prod-card">
                  <div className="av-prod-top">
                    <span className="av-prod-tag">PRODUCT</span>
                    <span className="av-prod-arrow">↗</span>
                  </div>
                  <h4>{p.name}</h4>
                  <p>{p.tag}</p>
                </Link>
              )
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA BANNER ── */}
      <section className="av-cta">
        <div className="av-cta-inner">
          <div className="av-cta-left">
            <p className="av-eyebrow-xs">SEE IT ON YOUR DATA</p>
            <h2 className="av-cta-title">Get a working demo of aivionSales.</h2>
            <p className="av-cta-desc">
              We'll run a tailored walkthrough on a sample of your own data — so you can judge the value,
              not just the slides.
            </p>
          </div>
          <div className="av-cta-right">
            <a href="/#contact" className="av-cta-btn-outline" onClick={(e)=>{e.preventDefault(); navigateToHomeSection('/#contact');}}>BOOK A DEMO &#8594;</a>
            <button className="av-cta-btn-ghost" onClick={(e)=>{e.preventDefault(); navigateToHomeSection('/#case-studies');}}>SEE IT IN ACTION</button>
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="av-footer">
        <div className="av-footer-inner">
          <div className="av-footer-brand">
            <div className="av-footer-logo">
              <span className="av-logo-aiv">AIV</span><span className="av-logo-ion">ION</span>
            </div>
            <p>Delivering meaningful outcomes through intelligent AI solutions.</p>
            <div className="av-footer-icons">
              <span className="av-icon">in</span>
              <span className="av-icon">&#9993;</span>
            </div>
          </div>
          <div className="av-footer-col">
            <p className="av-footer-heading">QUICK LINKS</p>
            <div className="footer-quick-links-grid">
              {footerLinks.map((l) => <a key={l.label} href={l.href} className="av-footer-link" onClick={(e)=>{e.preventDefault(); navigateToHomeSection(l.href);}}>{l.label}</a>)}
            </div>
          </div>
        </div>
        <div className="av-footer-bottom">
          <p>&#169; 2026 AIVION. All rights reserved.</p>
          
        </div>
      </footer>

    </div>
  );
}
